import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Loading,
  PageHeader,
  Toast,
} from '@/components/ui';
import { formatRelative } from '@/lib/format';

interface Provider {
  id: string;
  kind: string;
  key: string;
  name: string;
  driver: string;
  enabled: boolean;
  is_default: boolean;
  health_state: string;
  health_checked_at: string | null;
  health_error: string | null;
  health_source: string | null;
  health_call_failed_at: string | null;
  has_credentials: boolean;
  mocked: boolean;
  usable: boolean;
  quota: {
    unit: string;
    remaining: number;
    calls_remaining: number;
    exhausted: boolean;
  } | null;
  /** Why there is no balance, when there is no balance. */
  quota_note: string | null;
  config: Record<string, unknown>;
}

interface Response {
  data: Provider[];
  mock_mode: Record<string, boolean>;
  all_live: boolean;
}

const KIND_LABELS: Record<string, string> = {
  LLM: 'Language model',
  STT: 'Speech to text',
  TTS: 'Text to speech',
  TELEPHONY: 'Telephony',
  SIP: 'SIP',
  PAYMENT: 'Payments',
};

const HEALTH_TONES: Record<string, 'green' | 'amber' | 'red' | 'gray'> = {
  HEALTHY: 'green',
  DEGRADED: 'amber',
  FAILED: 'red',
  UNKNOWN: 'gray',
};

/**
 * Provider health, and what mock mode is overriding.
 *
 * The two are shown together deliberately. A provider can report perfectly
 * healthy while every call is quietly served by the mock, and that combination
 * is the one that costs an afternoon — "the key is right, the provider is up,
 * why is the bot saying the same thing every time".
 */
export default function ProviderStatus() {
  const query = useQuery({
    queryKey: ['admin-provider-status'],
    queryFn: () => api.get<Response>('/admin/provider-status'),
    // Five seconds: this is the page somebody opens when they are
    // changing keys and want to see the result, not a background
    // dashboard. It is one small query.
    refetchInterval: 5_000,
  });

  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  /**
   * Re-check one provider now, without leaving the page.
   *
   * This existed only on the Setup page, behind a key-entry form. So the
   * page that tells you a provider is broken had no way to ask again, and an
   * operator who had just fixed a key had to navigate away to find out
   * whether it took.
   *
   * It also releases the latch. A failure seen on a live call outranks a
   * background probe for ten minutes, which is what stops a passing probe
   * painting green over a provider that is failing every real call — but an
   * operator testing by hand and getting a pass is a deliberate override,
   * and does not have to wait the window out.
   */
  const recheck = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; provider: string; detail: string }>(
        `/admin/providers/${id}/test`,
        {},
      ),
    onSuccess: (result) => {
      void query.refetch();
      setToast({
        message: `${result.provider}: ${result.detail}`,
        tone: result.ok ? 'good' : 'bad',
      });
    },
    onError: (err) => setToast({ message: errorMessage(err, 'Test failed'), tone: 'bad' }),
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} retry={() => query.refetch()} />;

  const { data: providers, mock_mode: mockMode, all_live: allLive } = query.data;
  const mocked = Object.entries(mockMode).filter(([, on]) => on);

  const byKind = providers.reduce<Record<string, Provider[]>>((acc, provider) => {
    (acc[provider.kind] ??= []).push(provider);
    return acc;
  }, {});

  return (
    <div className="w-full">
      <PageHeader
        title="Provider status"
        description="Everything the voice pipeline depends on."
        actions={
          <Link
            to="/providers"
            className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            Manage providers →
          </Link>
        }
      />

      {/* Mock mode first: while any of these is on, nothing below tells you
          what a real call would do. */}
      <div
        className={
          allLive
            ? 'mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/50'
            : 'mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/50'
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p
              className={
                allLive
                  ? 'text-sm font-semibold text-emerald-900 dark:text-emerald-200'
                  : 'text-sm font-semibold text-amber-900 dark:text-amber-200'
              }
            >
              {allLive ? 'Every integration is live' : 'Some integrations are mocked'}
            </p>
            <p
              className={
                allLive
                  ? 'mt-0.5 text-xs text-emerald-800 dark:text-emerald-300'
                  : 'mt-0.5 text-xs text-amber-800 dark:text-amber-300'
              }
            >
              {allLive
                ? 'Calls use real providers and write back to real dialers.'
                : 'A mocked integration is answered by a stand-in, whatever its health says below. Turn these off one at a time in /opt/glee/.env, then restart.'}
            </p>
          </div>
          {!allLive && (
            <div className="flex flex-wrap gap-1.5">
              {mocked.map(([name]) => (
                <Badge key={name} tone="amber">
                  {name} mocked
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(byKind).map(([kind, group]) => (
          <Card key={kind} title={KIND_LABELS[kind] ?? kind} padded={false}>
            <ul className="divide-y divide-line">
              {group.map((provider) => (
                <li
                  key={provider.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink-900 dark:text-ink-50">
                        {provider.name}
                      </span>
                      {provider.is_default && <Badge tone="blue">Default</Badge>}
                      {!provider.enabled && <Badge tone="gray">Disabled</Badge>}
                    </div>
                    <p className="mt-0.5 font-mono text-2xs text-ink-500">
                      {provider.driver}
                      {typeof provider.config?.model === 'string' &&
                        ` · ${provider.config.model}`}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {provider.mocked && <Badge tone="amber">Mocked</Badge>}

                    {/* A provider with no credentials does not fail at call
                        time — it silently falls back to the mock. Saying so
                        here is the only place that is visible before a call. */}
                    {/* `usable`, not `has_credentials`. Telephony reads its
                        ARI URL, user and password from the environment and has
                        no credential row at all — so a box running every call
                        through a real Asterisk was told in red that it had no
                        credentials, on the page built to answer exactly that.
                        The backend already works this out per kind. */}
                    {!provider.usable && provider.driver !== 'mock' && (
                      <Badge tone="red">No credentials</Badge>
                    )}

                    {/* The reason, on hover, and where the verdict came
                        from. FAILED with nothing else sent an operator to the
                        log to find out which failure — and the commonest
                        cause by far, a key pasted from the wrong field, is
                        unrecognisable without the vendor's own message. */}
                    <Badge
                      tone={HEALTH_TONES[provider.health_state] ?? 'gray'}
                      title={provider.health_error ?? undefined}
                    >
                      {provider.health_state !== 'UNKNOWN'
                        ? provider.health_state
                        : provider.health_source === 'probe'
                          ? /* The timed probe ran and could not tell: this
                               driver has no credential check of its own, so
                               the only real evidence is live calls. Saying
                               "never checked" here would be a lie in the
                               other direction — it was checked a minute
                               ago. */
                            'Not probeable'
                          : 'Never checked'}
                      {provider.health_source === 'call' ? ' · on a call' : ''}
                    </Badge>

                    {/* What is left on the account.
                        A key that works and an account with nothing behind it
                        look identical here otherwise, and the second one ends
                        calls in silence. When there is no number, the reason
                        is shown rather than a blank — for most vendors it is
                        a key scope, and for the model vendors it is that they
                        publish no balance at all. */}
                    {provider.quota ? (
                      <span
                        className={
                          'w-44 text-right text-2xs tabular-nums ' +
                          (provider.quota.exhausted
                            ? 'font-medium text-red-600 dark:text-red-400'
                            : 'text-ink-500')
                        }
                      >
                        {provider.quota.remaining.toLocaleString()}{' '}
                        {provider.quota.unit} left
                        <span className="block text-ink-400">
                          ~{provider.quota.calls_remaining.toLocaleString()} calls
                        </span>
                      </span>
                    ) : provider.quota_note ? (
                      <span
                        className="w-44 text-right text-2xs text-ink-400"
                        title={provider.quota_note}
                      >
                        no balance reported
                      </span>
                    ) : (
                      <span className="w-44" />
                    )}

                    {/* When this was last asked, labelled. This is a list
                        rather than a table, so there is no column header to
                        carry the meaning, and a bare "2 min ago" next to a
                        health badge could as easily be read as when the
                        provider last took a call.

                        A background job re-probes every provider once a
                        minute, so this should normally read in seconds or a
                        minute. Anything older means the worker is not
                        running — which is itself worth seeing, and is why
                        the exact time is on hover. */}
                    <span
                      className="w-24 text-right text-2xs text-ink-400"
                      title={
                        provider.health_checked_at
                          ? new Date(provider.health_checked_at).toLocaleString()
                          : 'this provider has not been checked since it was configured'
                      }
                    >
                      <span className="block text-ink-300 dark:text-ink-600">
                        checked
                      </span>
                      {provider.health_checked_at
                        ? formatRelative(provider.health_checked_at)
                        : 'never'}
                    </span>

                    {/* Ask the vendor now. Offered only where it means
                        something: a mocked provider has no vendor to ask, and
                        one with no credentials would fail for a reason the
                        page is already showing in red. */}
                    {!provider.mocked && provider.usable && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={recheck.isPending && recheck.variables === provider.id}
                        onClick={() => recheck.mutate(provider.id)}
                      >
                        Re-check
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      {providers.some((p) => !p.usable && p.enabled) && (
        <p className="mt-4 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
          A provider marked <strong>No credentials</strong> is enabled but cannot
          be built, so calls fall back to the mock rather than failing. That is
          deliberate — a degraded bot that still greets and dispositions the
          caller beats dead air — but it means the flag above is the only warning
          you get.
        </p>
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
